<template>
    <div class="bg-white overflow-hidden shadow-xl sm:rounded-lg p-6">
        <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
                <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">Current plan</p>
                <div class="mt-2 flex flex-wrap items-center gap-2">
                    <span class="text-2xl font-bold text-gray-900">{{ planName }}</span>
                    <span
                        v-if="isPro && tierRangeLabel"
                        class="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-700"
                    >
                        {{ tierRangeLabel }}
                    </span>
                    <span
                        v-if="hasPaymentProblem"
                        class="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800"
                    >
                        Payment problem
                    </span>
                    <span
                        v-else-if="isCancelling"
                        class="inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-semibold text-yellow-800"
                    >
                        Cancelling
                    </span>
                    <span
                        v-else-if="isPro"
                        class="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800"
                    >
                        Active
                    </span>
                </div>
            </div>
            <div v-if="isPro && currentTier" class="text-right">
                <p class="text-3xl font-bold text-rent-risk-blue-400">{{ priceOnly(currentTier.price) }}</p>
                <p class="text-sm text-gray-500">per year</p>
            </div>
        </div>

        <p
            v-if="hasPaymentProblem"
            class="mt-3 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700"
        >
            We couldn't charge your card for RiskGuard Pro. Update your payment method below to keep your Pro features.
        </p>

        <div v-if="isPro" class="mt-5 grid grid-cols-1 gap-4 rounded-lg bg-gray-50 p-4 sm:grid-cols-3">
            <div>
                <p class="text-sm text-gray-500">{{ isCancelling ? 'Ends on' : 'Next billing date' }}</p>
                <p class="text-sm font-semibold text-gray-900">{{ renewalLabel }}</p>
            </div>
            <div>
                <p class="text-sm text-gray-500">Estimated renewal</p>
                <p class="text-sm font-semibold text-gray-900">{{ money2(estimatedRenewalPrice ?? currentTier.price) }}</p>
            </div>
            <div>
                <p class="text-sm text-gray-500">Billing cycle</p>
                <p class="text-sm font-semibold text-gray-900">Annual</p>
            </div>
        </div>

        <p v-else class="mt-3 text-sm text-gray-600">
            You're on the free Basic plan. Upgrade to Pro for account-wide features across every property.
        </p>

        <div class="mt-6 flex flex-wrap gap-3">
            <button type="button" class="primaryButton !mt-0" @click="$emit('change-plan')">
                {{ changePlanLabel }}
            </button>
            <button
                v-if="isPro"
                type="button"
                class="whiteButton !mt-0"
                @click="$emit('manage-payment')"
            >
                Manage payment
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'CurrentPlanCard',
    props: {
        proPlan: {
            type: Object,
            default: null,
        },
    },
    emits: ['change-plan', 'manage-payment'],
    computed: {
        isPro() {
            return this.proPlan?.state === 'pro';
        },
        hasPaymentProblem() {
            return Boolean(this.proPlan?.has_payment_problem);
        },
        isCancelling() {
            return Boolean(this.proPlan?.is_cancelling);
        },
        currentTier() {
            return this.proPlan?.current_tier ?? null;
        },
        planName() {
            return this.isPro ? 'Pro' : 'Basic';
        },
        tierRangeLabel() {
            const tier = this.currentTier;

            if (!tier) {
                return '';
            }

            if (!tier.max) {
                return `${tier.min || 1}+ units`;
            }

            return `${tier.min || 1}–${tier.max} units`;
        },
        changePlanLabel() {
            if (this.proPlan?.state === 'basic') {
                return 'Upgrade to Pro';
            }

            return 'Change plan';
        },
        renewalLabel() {
            const date = this.isCancelling ? this.proPlan?.cancel_at : this.proPlan?.renewal_date;

            return date ? new Date(date).toLocaleDateString() : '—';
        },
        estimatedRenewalPrice() {
            return this.proPlan?.next_property_tier_change?.to_price ?? null;
        },
    },
    methods: {
        priceOnly(price) {
            return `$${Number(price).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
        },
        money2(price) {
            return `$${Number(price).toFixed(2)}`;
        },
    },
};
</script>
